import { staleTimes, useQuery } from '@platform/api-client';
import type { Operation, ResponseBody, TypedClient } from '@platform/openapi';
import { queryKeys, usePageContext } from '../usePageContext.ts';

export type Geography = ResponseBody<Operation<'/admin/geo-areas', 'get'>>;
export type Governorate = Geography['governorates'][number];

export type GeoNames = (code: string | null) => string;

export const namesOf = (geography: Geography | undefined): GeoNames => {
  const names = new Map(
    (geography?.governorates ?? []).flatMap((governorate) => [
      [governorate.code, governorate.name] as const,
      ...governorate.delegations.map((delegation) => [delegation.code, delegation.name] as const),
    ]),
  );
  return (code) => (code === null ? '—' : (names.get(code) ?? code));
};

const sameName = (left: string, right: string): boolean =>
  left.localeCompare(right, undefined, { sensitivity: 'base' }) === 0;

export const governorateNamed = (
  geography: Geography | undefined,
  name: string | null,
): Governorate | undefined =>
  name === null ? undefined : geography?.governorates.find((governorate) => sameName(governorate.name, name));

export const delegationNamed = (governorate: Governorate | undefined, name: string | null): string | null =>
  name === null
    ? null
    : (governorate?.delegations.find((delegation) => sameName(delegation.name, name))?.code ?? null);

export const useGeoAreas = (api: TypedClient) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.geoAreas(tenantId),
    queryFn: () => api.get('/admin/geo-areas', {}),
    staleTime: staleTimes.reference,
  });
};
